import { useEffect, useState } from "react";
import {
  OrganizationStatus,
  usePatchOrganizationStatus,
} from "@/features-root-user/service/organization";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  PopoverForm,
  PopoverFormButton,
  PopoverFormSuccess,
} from "@/components/ui/popover-form";
import { StatusSelect } from "@/components/status-select";
import { STATUS_BADGE_STYLES } from "../constants/status-badges";

type FormState = "idle" | "loading" | "success";

interface OrganizationStatusFormProps {
  organizationId: string;
  status: OrganizationStatus;
  popoverIdentifier: string;
}

export function OrganizationStatusForm({
  organizationId,
  status,
  popoverIdentifier,
}: OrganizationStatusFormProps) {
  const [formState, setFormState] = useState<FormState>("idle");
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] =
    useState<OrganizationStatus>(status);
  const updateMutation = usePatchOrganizationStatus();

  const handleSubmit = () => {
    if (!selectedStatus) return;
    setFormState("loading");
    updateMutation.mutate(
      { id: organizationId, status: selectedStatus },
      {
        onSuccess: () => {
          setFormState("success");
          toast.success("Organization status updated successfully!");
          setTimeout(() => {
            setOpen(false);
            setFormState("idle");
          }, 1000);
        },
        onError: () => {
          toast.error("Failed to update status");
          setFormState("idle");
        },
      },
    );
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <PopoverForm
      title={popoverIdentifier}
      name={
        <Badge
          variant="outline"
          className={cn("capitalize", STATUS_BADGE_STYLES[selectedStatus])}
        >
          {selectedStatus}
        </Badge>
      }
      open={open}
      setOpen={setOpen}
      width="320px"
      showCloseButton={formState !== "success"}
      showSuccess={formState === "success"}
      openChild={
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="space-y-4 p-4"
        >
          <div className="mb-4 space-y-2">
            <label
              htmlFor="status"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Status
            </label>
            <StatusSelect
              id="status"
              value={selectedStatus}
              onValueChange={(value) =>
                setSelectedStatus(value as OrganizationStatus)
              }
              placeholder="Select Status"
            />
            <p className="text-muted-foreground text-xs tracking-tight">
              Change the organization's current status.
            </p>
          </div>
          <PopoverFormButton loading={formState === "loading"} text="Update" />
        </form>
      }
      successChild={
        <PopoverFormSuccess
          title="Successfully updated!"
          description="The organization's status has been updated."
        />
      }
    />
  );
}
